import React, { useEffect, useMemo } from "react";
import { Button, Container, Paper, Group, Switch, Stack } from "@mantine/core";
import { useForm, Controller } from "react-hook-form";
import { TextInput } from "react-hook-form-mantine";

import {
  useGetSystemSettings,
  useUpdateSystemSetting,
} from "../../api/systemSettings";
import { extractDirtyData } from "../../utils/dirtyData";
import { web_systemSettingsSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";

function coerceBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return Boolean(value);
}

function EditSystemSettings() {
  const { data, isPending } = useGetSystemSettings();
  const { mutateAsync, isPending: isSaving } = useUpdateSystemSetting();

  const defaultValues = useMemo(() => {
    if (!data) {
      return {
        minIosVersion: "",
        minAndroidVersion: "",
        forceExpoUpdatesIos: false,
        forceExpoUpdatesAndroid: false,
        useNewMobileApp: false,
        enableSetupScreen: false,
        iosAppStoreUrl: "",
        androidAppStoreUrl: "",
      };
    }
    return {
      minIosVersion: data.minIosVersion ?? "",
      minAndroidVersion: data.minAndroidVersion ?? "",
      forceExpoUpdatesIos: coerceBoolean(data.forceExpoUpdatesIos),
      forceExpoUpdatesAndroid: coerceBoolean(data.forceExpoUpdatesAndroid),
      useNewMobileApp: coerceBoolean(data.useNewMobileApp),
      enableSetupScreen: coerceBoolean(data.enableSetupScreen),
      iosAppStoreUrl: data.iosAppStoreUrl ?? "",
      androidAppStoreUrl: data.androidAppStoreUrl ?? "",
    };
  }, [data]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm({
    defaultValues,
    resolver: resolver(web_systemSettingsSchema, { allErrors: true }),
    mode: "onTouched",
  });

  useEffect(() => {
    if (data) {
      reset(defaultValues);
    }
  }, [data, reset, defaultValues]);

  const onSubmit = async (formData) => {
    const changed = extractDirtyData(formData, dirtyFields);

    mutateAsync(changed, {
      onSuccess: () => {
        reset(formData);
      },
    });
  };

  return (
    <Container size={700}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <TextInput
              name="minIosVersion"
              control={control}
              label="Minimum iOS Version"
              placeholder="e.g. 1.0.0"
              withAsterisk
            />

            <TextInput
              name="minAndroidVersion"
              control={control}
              label="Minimum Android Version"
              placeholder="e.g. 1.0.0"
              withAsterisk
            />

            <Controller
              name="forceExpoUpdatesIos"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  label="Force Expo Updates iOS"
                  color="green"
                />
              )}
            />

            <Controller
              name="forceExpoUpdatesAndroid"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  label="Force Expo Updates Android"
                  color="green"
                />
              )}
            />

            <Controller
              name="useNewMobileApp"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  label="Use New Mobile App"
                  color="green"
                />
              )}
            />

            <Controller
              name="enableSetupScreen"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                  label="Set Up screen"
                  description="Shows the Setup checklist in the organizer navigation for every event"
                  color="green"
                />
              )}
            />

            <TextInput
              name="iosAppStoreUrl"
              control={control}
              label="iOS App Store URL"
              placeholder="https://apps.apple.com/app/..."
              withAsterisk
            />

            <TextInput
              name="androidAppStoreUrl"
              control={control}
              label="Android App Store URL"
              placeholder="https://play.google.com/store/apps/details?id=..."
              withAsterisk
            />

            <Group justify="flex-end" mt="sm">
              <Button type="submit" loading={isPending || isSaving}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

export default EditSystemSettings;
